import { useState, useMemo } from "react";
import Chart from "react-apexcharts";
import { reduzirNome } from "../../utils/reduzirNome";

export default function GraficoBar({ title, labels, values, width, height, legend }) {
    const parsedValues = values.map(v => Number(v ?? 0));

    // Ranges de cores e filtros
    const ranges = [
        { label: "≤ 5.000", color: "#3b82f6", check: (v) => v <= 5000 },
        { label: "5.001 – 10.000", color: "#10b981", check: (v) => v > 5000 && v <= 10000 },
        { label: "10.001 – 50.000", color: "#f59e0b", check: (v) => v > 10000 && v <= 50000 },
        { label: "> 50.000", color: "#ef4444", check: (v) => v > 50000 },
    ];

    // Estado para saber quais ranges estão ativos
    const [activeRanges, setActiveRanges] = useState(ranges.map(() => true));

    // Função para alternar o clique na legenda
    const toggleRange = (index) => {
        setActiveRanges((prev) =>
            prev.map((active, i) => (i === index ? !active : active))
        );
    };

    // Filtrar dados com base nos ranges ativos
    const filtered = useMemo(() => {
        return labels
            .map((label, i) => ({ label, value: parsedValues[i] }))
            .filter(({ value }) =>
               value > 0 && ranges.some((r, idx) => activeRanges[idx] && r.check(value))
            );
    }, [labels, parsedValues, activeRanges]);

    const barColors = filtered.map(({ value }) => {
        if (value > 50000) return "#ef4444";
        if (value > 10000) return "#f59e0b";
        if (value > 5000) return "#10b981";
        return "#3b82f6";
    });

    const options = {
        chart: {
            type: "bar",
            toolbar: { show: false }
        },
        plotOptions: {
            bar: { borderRadius: 6, columnWidth: "60%", distributed: true },
        },
        colors: barColors,
        xaxis: {
            categories: filtered.map((d) => reduzirNome(d.label)),
            labels: {
                rotate: -45
            },
        },
        dataLabels: { enabled: false },
        yaxis: {
            floating: false,
            logarithmic: true,
            labels: {
                formatter: (value) =>
                    Number(value).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                    }),
            },
        },
        tooltip: {
            y: {
                formatter: (value) =>
                    Number(value).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                    }),
            },
            x: {
                show: true,
                formatter: function (val, opts) {
                    return labels[opts.dataPointIndex]; // mostra nome completo
                },
            },
        },
        legend: { show: false },
    };

    const series = [{ name: "Valor (R$)", data: filtered.map((d) => d.value) }];

    return (
        <div className="bg-white p-4 rounded-2xl shadow-md fade-in">
            <h2 className="text-xl font-bold mb-2">{title}</h2>

            {filtered.length > 0 ? (
                <>
                    <Chart width={width} options={options} series={series} type="bar" height={height} />

                    {legend && (
                        <div className="mt-4 flex flex-wrap gap-4 text-sm">
                            {ranges.map((range, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => toggleRange(idx)}
                                    className={`flex items-center gap-1 px-2 py-1 rounded ${activeRanges[idx] ? "opacity-100" : "opacity-40"
                                        }`}
                                >
                                    <span
                                        className="w-4 h-4 rounded"
                                        style={{ background: range.color }}
                                    ></span>
                                    <span>{range.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                "Não existem dados disponíveis para o intervalo selecionado."
            )}
        </div>
    );
}